/**
 * 交易 / 理财收益 — frontend-api-integration.md 2026-09-16 团队页收益接口
 * 金额均为最小单位字符串；禁止 Number 参与资金计算。
 */
import { request } from '@/common/services/request';

export type EarningsPeriod = '1D' | '1W' | '1M' | '3M' | 'YTD' | '1Y' | 'ALL';
export type EarningsAsset = 'Joule' | 'PANTA';
export type EarningsKind = 'PERSONAL' | 'TEAM' | 'UNCLASSIFIED';
export type EarningsScope = 'trading' | 'wealth';

export interface EarningsOverviewItem {
  kind: EarningsKind;
  totalAmount: string;
  periodAmount: string;
  creditedAvailable?: string | null;
  creditedFrozen?: string | null;
  splitUnavailableCount?: number;
}

export interface EarningsMeta {
  scope: string;
  asset: EarningsAsset | string;
  decimals: number;
  period: EarningsPeriod | string;
  timezone: string;
  asOf: string;
}

export interface EarningsOverview extends EarningsMeta {
  activity?: {
    metric: 'SETTLED_STAKE' | 'SUBSCRIBED_PANTA';
    asset: 'Joule' | 'PANTA';
    decimals: number;
    personalAmount: string;
    teamAmount: string;
    periodStartAt: string | null;
    asOf: string;
  };
  totalAmount: string;
  periodAmount: string;
  items: EarningsOverviewItem[];
}

export interface EarningsTrendPoint {
  bucketStart: string;
  kind: EarningsKind;
  amount: string;
  cumulative: string;
}

export interface EarningsTrend extends EarningsMeta {
  granularity: 'hour' | 'day' | string;
  items: EarningsTrendPoint[];
}

export interface EarningsRecord {
  id: string;
  kind: EarningsKind | string;
  type: string;
  businessId: string;
  ledgerEntryId?: string | null;
  amount: string;
  availableAmount?: string | null;
  frozenAmount?: string | null;
  creditedAt: string;
  contributorName?: string | null;
  status?: string | null;
}

export interface EarningsRecordsPage extends EarningsMeta {
  items: EarningsRecord[];
  nextOffset: number | null;
}

export interface EarningsQuery {
  period?: EarningsPeriod;
  asset?: EarningsAsset;
  kind?: EarningsKind;
  offset?: number;
  limit?: number;
}

const normalizePeriod = (period?: string): EarningsPeriod => {
  const p = String(period || '1W').toUpperCase();
  const allowed: EarningsPeriod[] = ['1D', '1W', '1M', '3M', 'YTD', '1Y', 'ALL'];
  return (allowed.includes(p as EarningsPeriod) ? p : '1W') as EarningsPeriod;
};

const buildQuery = (params: EarningsQuery = {}, defaults?: { asset?: EarningsAsset }) => {
  const data: Record<string, string | number> = {
    period: normalizePeriod(params.period),
  };
  const asset = params.asset ?? defaults?.asset;
  if (asset) data.asset = asset;
  if (params.kind) data.kind = params.kind;
  if (params.offset != null) {
    data.offset = Math.max(0, Math.min(100000, Number(params.offset) || 0));
  }
  if (params.limit != null) {
    data.limit = Math.max(1, Math.min(100, Number(params.limit) || 20));
  }
  return data;
};

const overviewUrl = (scope: EarningsScope) =>
  scope === 'trading'
    ? '/v1/trading/earnings/overview'
    : '/v1/wealth/earnings/overview';

const trendUrl = (scope: EarningsScope) =>
  scope === 'trading'
    ? '/v1/trading/earnings/trend'
    : '/v1/wealth/earnings/trend';

const recordsUrl = (scope: EarningsScope) =>
  scope === 'trading'
    ? '/v1/trading/earnings/records'
    : '/v1/wealth/earnings/records';

const teamRewardsUrl = (scope: EarningsScope) =>
  scope === 'trading'
    ? '/v1/trading/team/rewards'
    : '/v1/wealth/team/rewards';

export const getEarningsOverview = (
  scope: EarningsScope,
  params: EarningsQuery = {}
): Promise<EarningsOverview> =>
  request({
    url: overviewUrl(scope),
    method: 'GET',
    data: buildQuery(params, {
      asset: scope === 'trading' ? 'Joule' : 'PANTA',
    }),
  });

export const getEarningsTrend = (
  scope: EarningsScope,
  params: EarningsQuery = {}
): Promise<EarningsTrend> =>
  request({
    url: trendUrl(scope),
    method: 'GET',
    data: buildQuery(params, {
      asset: scope === 'trading' ? 'Joule' : 'PANTA',
    }),
  });

export const listEarningsRecords = (
  scope: EarningsScope,
  params: EarningsQuery = {}
): Promise<EarningsRecordsPage> =>
  request({
    url: recordsUrl(scope),
    method: 'GET',
    data: buildQuery(
      { kind: 'PERSONAL', limit: 20, ...params },
      { asset: scope === 'trading' ? 'Joule' : 'PANTA' }
    ),
  });

export const listTeamEarningsRewards = (
  scope: EarningsScope,
  params: EarningsQuery = {}
): Promise<EarningsRecordsPage> =>
  request({
    url: teamRewardsUrl(scope),
    method: 'GET',
    data: buildQuery(
      { limit: 20, ...params },
      { asset: scope === 'trading' ? 'Joule' : 'PANTA' }
    ),
  });

export const pickEarningsKind = (
  overview: EarningsOverview | null | undefined,
  kind: EarningsKind
): EarningsOverviewItem | null => {
  const item = (overview?.items || []).find((i) => i.kind === kind);
  return item || null;
};

/** 详情路由：type → path（businessId） */
export const earningsDetailPath = (
  type: string,
  businessId: string
): string | null => {
  const id = encodeURIComponent(businessId);
  switch (String(type || '').toUpperCase()) {
    case 'ORDER':
      return `/v1/orders/${id}`;
    case 'INSURANCE_PAYOUT':
      return `/v1/insurance/claims/${id}`;
    case 'NETWORK_DIFFERENTIAL':
    case 'NETWORK_PEER':
      return `/v1/network/rewards/${id}`;
    case 'WEALTH_DAILY_REWARD':
      return `/v1/wealth/daily-rewards/${id}`;
    case 'WEALTH_TEAM_REWARD':
      return `/v1/wealth/rewards/${id}`;
    case 'EVENT_INSURANCE_PAYOUT':
      return `/v1/event-insurance/payouts/${id}`;
    case 'EVENT_INSURANCE_PEER':
      return `/v1/event-insurance/peers/${id}`;
    default:
      return null;
  }
};
